(() => {
  const CART_DIRTY_KEY = 'herculesCartDirtyAt';
  const CART_REFRESHED_KEY = 'herculesCartRefreshedAt';

  const isCartPath = (pathname) => pathname === '/cart' || pathname.endsWith('/cart');
  const isCartAddPath = (pathname) => pathname.endsWith('/cart/add') || pathname.endsWith('/cart/add.js');

  const parseUrl = (url) => {
    try {
      return new URL(url, window.location.origin);
    } catch (error) {
      return null;
    }
  };

  const isCartUrl = (url) => {
    const parsed = parseUrl(url);
    return Boolean(parsed && parsed.origin === window.location.origin && isCartPath(parsed.pathname));
  };

  const isCartAddUrl = (url) => {
    const parsed = parseUrl(url);
    return Boolean(parsed && parsed.origin === window.location.origin && isCartAddPath(parsed.pathname));
  };

  const freshCartUrl = (stamp = getDirtyAt() || String(Date.now())) => {
    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = new URL(`${root}cart`, window.location.origin);
    cartUrl.searchParams.set('_hercules_cart', stamp);
    return cartUrl.toString();
  };

  const refreshCartLinks = (stamp) => {
    document.querySelectorAll('a[href]').forEach((link) => {
      if (isCartUrl(link.href)) {
        link.href = freshCartUrl(stamp);
      }
    });
  };

  const markCartDirty = () => {
    const stamp = String(Date.now());
    try {
      sessionStorage.setItem(CART_DIRTY_KEY, stamp);
    } catch (error) {}
    document.documentElement.dataset.herculesCartDirty = 'true';
    refreshCartLinks(stamp);
    scheduleCartStateRefresh();
  };

  const clearCartDirty = () => {
    try {
      sessionStorage.removeItem(CART_DIRTY_KEY);
      sessionStorage.removeItem(CART_REFRESHED_KEY);
    } catch (error) {}
    delete document.documentElement.dataset.herculesCartDirty;
  };

  const getDirtyAt = () => {
    try {
      return sessionStorage.getItem(CART_DIRTY_KEY);
    } catch (error) {
      return null;
    }
  };

  const getCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js?hercules=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Cart request failed');
    }

    return response.json();
  };

  const getCartSectionIds = () => [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((item) => item.dataset.sectionId)
        .filter(Boolean)
    ),
  ];

  const getCartSections = async () => {
    const sectionIds = getCartSectionIds();
    if (!sectionIds.length) return {};

    const root = window.Shopify?.routes?.root || '/';
    const cartPath = window.Shopify?.routes?.cart_url || `${root}cart`;
    const sectionUrl = new URL(cartPath, window.location.origin);
    sectionUrl.searchParams.set('sections', sectionIds.join(','));
    sectionUrl.searchParams.set('sections_url', cartPath);
    sectionUrl.searchParams.set('_hercules_cart_sections', String(Date.now()));

    const response = await fetch(sectionUrl.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) return {};

    return response.json();
  };

  const updateCartCount = (cart) => {
    const count = cart.item_count || 0;
    const countTargets = document.querySelectorAll(
      '.cart-bubble__text-count, [data-cart-count], .cart-count, .cart-count-bubble span[aria-hidden="true"], #cart-icon-bubble span[aria-hidden="true"]'
    );

    countTargets.forEach((target) => {
      target.textContent = count;
    });

    document.querySelectorAll('.cart-bubble, .cart-count-bubble').forEach((bubble) => {
      bubble.hidden = count === 0;
      bubble.classList.toggle('visually-hidden', count === 0);
    });
  };

  const updateCartDrawer = (sections = {}) => {
    getCartSectionIds().forEach((sectionId) => {
      const sectionHtml = sections[sectionId];
      if (!sectionHtml) return;

      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const nextItems = html.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
      const currentItems = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);

      if (nextItems && currentItems) {
        currentItems.innerHTML = nextItems.innerHTML;
      } else {
        const nextSection = html.getElementById(`shopify-section-${sectionId}`);
        const currentSection = document.getElementById(`shopify-section-${sectionId}`);

        if (nextSection && currentSection) {
          currentSection.innerHTML = nextSection.innerHTML;
        }
      }

      const nextDialog = html.querySelector('cart-drawer-component dialog');
      const currentDialog = document.querySelector('cart-drawer-component dialog');

      if (nextDialog && currentDialog) {
        currentDialog.className = nextDialog.className;
        currentDialog.setAttribute('aria-labelledby', nextDialog.getAttribute('aria-labelledby') || '');
      }
    });
  };

  const dispatchCartUpdate = (cart, sections = {}) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'hercules-cart-immediate-sync',
          data: {
            source: 'hercules-cart-immediate-sync',
            itemCount: cart.item_count || 0,
            sections,
          },
        },
      })
    );
  };

  async function refreshCartState() {
    try {
      const [cart, sections] = await Promise.all([getCart(), getCartSections()]);

      updateCartCount(cart);
      updateCartDrawer(sections);
      window.dispatchEvent(new CustomEvent('hercules:cart-updated', { detail: { cart } }));
      dispatchCartUpdate(cart, sections);
    } catch (error) {}
  }

  const scheduleCartStateRefresh = () => {
    refreshCartState();
    window.setTimeout(refreshCartState, 250);
    window.setTimeout(refreshCartState, 800);
  };

  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (isCartAddUrl(args[0]?.url || args[0]) && response.ok) {
        markCartDirty();
      }
      return response;
    };
  }

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    this.__herculesCartAdd = isCartAddUrl(url);
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function send(...args) {
    if (this.__herculesCartAdd) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) {
          markCartDirty();
        }
      });
    }

    return originalSend.apply(this, args);
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[type="submit"], button[name="add"], [data-add-to-cart]');
      const form = button?.closest?.('form');

      if (form && isCartAddUrl(form.action || form.getAttribute('action'))) {
        setTimeout(markCartDirty, 0);
      }

      const cartTrigger = event.target.closest?.(
        'a[href*="/cart"], #cart-icon-bubble, .header__icon--cart, .cart-icon, [data-cart-icon], [data-cart-drawer-toggle]'
      );

      if (cartTrigger || event.target.closest?.('a[href*="/cart"]')) {
        scheduleCartStateRefresh();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (isCartAddUrl(form.action || form.getAttribute('action'))) {
        setTimeout(markCartDirty, 0);
      }
    },
    true
  );

  const refreshCartPageOnceIfNeeded = () => {
    if (!isCartPath(window.location.pathname)) return;

    const dirtyAt = getDirtyAt();
    if (!dirtyAt) return;

    let refreshedAt = null;
    try {
      refreshedAt = sessionStorage.getItem(CART_REFRESHED_KEY);
    } catch (error) {}

    if (refreshedAt === dirtyAt) {
      clearCartDirty();
      return;
    }

    try {
      sessionStorage.setItem(CART_REFRESHED_KEY, dirtyAt);
    } catch (error) {}

    const cartUrl = new URL(window.location.href);
    cartUrl.searchParams.set('_hercules_cart', dirtyAt);
    window.location.replace(cartUrl.toString());
  };

  window.addEventListener('pageshow', () => {
    scheduleCartStateRefresh();
    refreshCartPageOnceIfNeeded();
  });

  document.addEventListener('DOMContentLoaded', () => {
    scheduleCartStateRefresh();
    refreshCartPageOnceIfNeeded();
  });

  window.HerculesCartSync = {
    refresh: scheduleCartStateRefresh,
  };
})();
